import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IChannel } from '@/entities/channel';
import { AvitoConnectForm } from '@/features/channels';
import { useAvitoDisconnect } from '@/features/channels/model/avito/useAvitoConnect';
import { useGetConfirmation } from '@/shared/ui/Confirmations/confirmation';
import { Button } from '@/shared/ui/lib/Button';
import { useNotifications } from '@/shared/ui/Notifications';

import { SettingBlockItem } from './SettingBlockItem';

type AvitoAuthorizationBlockProps = {
  channel: Pick<IChannel, 'id' | 'name'>;
};

export const AvitoAuthorizationBlock = ({
  channel,
}: AvitoAuthorizationBlockProps) => {
  const { t } = useTranslation();
  const { getConfirmation } = useGetConfirmation();
  const { addNotification } = useNotifications();
  const { disconnect, isPending } = useAvitoDisconnect();
  const [isConnectDialogOpen, setConnectDialogOpen] = useState(false);

  const handleDisconnect = async (openConnectDialog = false) => {
    const isConfirmed = await getConfirmation({
      title: t(
        openConnectDialog
          ? 'channels:settings.avitoAuthorization.replaceDialog.title'
          : 'channels:settings.avitoAuthorization.deleteDialog.title'
      ),
      description: t(
        openConnectDialog
          ? 'channels:settings.avitoAuthorization.replaceDialog.description'
          : 'channels:settings.avitoAuthorization.deleteDialog.description'
      ),
      confirmText: t(
        openConnectDialog
          ? 'channels:settings.avitoAuthorization.replaceDialog.confirm'
          : 'channels:settings.avitoAuthorization.deleteDialog.confirm'
      ),
      shouldCloseOnConfirm: true,
      priorityButton: 'confirm',
    });

    if (!isConfirmed) return;

    await disconnect(channel.id, () => {
      if (openConnectDialog) {
        setConnectDialogOpen(true);
        return;
      }

      addNotification({
        type: 'success',
        title: t('channels:settings.avitoAuthorization.deleteDialog.success'),
      });
    });
  };

  return (
    <>
      <SettingBlockItem title={t('channels:settings.avitoAuthorization.title')}>
        <Button
          variant="text"
          disabled={isPending}
          onClick={() => handleDisconnect(true)}
        >
          {t('channels:settings.avitoAuthorization.replace')}
        </Button>
        <Button
          variant="text"
          color="error"
          loading={isPending}
          onClick={() => handleDisconnect()}
        >
          {t('channels:settings.avitoAuthorization.delete')}
        </Button>
      </SettingBlockItem>
      <AvitoConnectForm
        open={isConnectDialogOpen}
        onClose={() => setConnectDialogOpen(false)}
        onAuthorize={() => {
          setConnectDialogOpen(false);
          addNotification({
            type: 'success',
            title: t(
              'channels:settings.avitoAuthorization.replaceDialog.success'
            ),
          });
        }}
        channel={channel}
      />
    </>
  );
};
